import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseClient';
import LoadingScreen from '../../components/LoadingScreen';
import ProfileCard from '../../components/profiles/ProfileCard';

const ParticipantProfilesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileType, setProfileType] = useState('all');
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9); // Show 9 profiles per page (3x3 grid)
  const [totalPages, setTotalPages] = useState(1);
  const [currentProfiles, setCurrentProfiles] = useState([]);

  // Fetch all profiles
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        setLoading(true);
        
        // Fetch active user types
        const { data: userTypes, error: userTypesError } = await supabase
          .from('user_types')
          .select('*')
          .eq('is_active', true);
          
        if (userTypesError) throw userTypesError;
        
        // Create a map of active user types
        const activeUserTypes = {};
        userTypes.forEach(type => {
          if (!activeUserTypes[type.user_id]) {
            activeUserTypes[type.user_id] = [];
          }
          activeUserTypes[type.user_id].push(type.type);
        });
        
        console.log('Active user types:', activeUserTypes);
        
        // Get user profiles
        const { data: userProfiles, error: userProfileError } = await supabase
          .from('user_profiles')
          .select('*');
          
        if (userProfileError) throw userProfileError;
        
        // Fetch details for each profile type
        const [startupData, investorData, serviceData, pharmaData] = await Promise.all([
          // Startups - modify query to ensure we get ALL products for ALL users
          // Using RPC call to bypass row-level security if applicable
          supabase.rpc('get_all_products'),
            
          // Investors
          supabase.from('investor_profiles')
            .select('*'),
            
          // Service Providers
          supabase.from('service_provider_profiles')
            .select('*'),
            
          // Pharmaceutical Companies
          supabase.from('pharmaceutical_profiles')
            .select('*')
        ]);
        
        // Check if there was an error with the products query
        if (startupData.error) {
          console.error('Error fetching products:', startupData.error);
          
          // Fall back to standard query if RPC fails
          const fallbackQuery = await supabase.from('products')
            .select('id, name, development_stage, product_category, user_id')
            .order('created_at', { ascending: false });
            
          if (fallbackQuery.error) {
            console.error('Fallback query also failed:', fallbackQuery.error);
            startupData.data = [];
          } else {
            console.log('Using fallback query results');
            startupData.data = fallbackQuery.data;
          }
        }
        
        // Get product counts for each user
        const productCountsByUserId = {};
        if (startupData.data) {
          startupData.data.forEach(product => {
            if (!productCountsByUserId[product.user_id]) {
              productCountsByUserId[product.user_id] = [];
            }
            productCountsByUserId[product.user_id].push(product);
          });
        }
        
        console.log('Product counts by user:', productCountsByUserId);
        
        // Create all possible profiles for all users with active types
        const allProfiles = [];
        
        // For each user with active types
        Object.keys(activeUserTypes).forEach(userId => {
          const userActiveTypes = activeUserTypes[userId];
          const userProfile = userProfiles.find(profile => profile.user_id === userId);
          
          if (!userProfile) return; // Skip if no user profile found
          
          // For each active type, create a profile entry
          userActiveTypes.forEach(type => {
            let profileData = null;
            
            // Get type-specific data
            if (type === 'startup') {
              const products = productCountsByUserId[userId] || [];
              profileData = {
                products: products,
                mainProduct: products[0] || null
              };
              
              // Debug log to check product counts
              console.log(`User ${userId} (${userProfile.name}) has ${products.length} products`);
            } else if (type === 'investor' && investorData.data) {
              profileData = investorData.data.find(p => p.user_id === userId) || null;
            } else if (type === 'service_provider' && serviceData.data) {
              profileData = serviceData.data.find(p => p.user_id === userId) || null;
            } else if (type === 'pharmaceutical' && pharmaData.data) {
              profileData = pharmaData.data.find(p => p.user_id === userId) || null;
            }
            
            // Only add if we have profile data or it's a startup (which might not have products)
            if (profileData || type === 'startup') {
              allProfiles.push({
                ...userProfile,
                profileType: type,
                profileData: profileData
              });
            }
          });
        });
        
        console.log('All profiles:', allProfiles);
        
        setProfiles(allProfiles);
        setFilteredProfiles(allProfiles);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching profiles:', err);
        setError('Failed to load profiles. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchProfiles();
  }, []);
  
  // Apply filters based on profile type and search term
  useEffect(() => {
    let result = [...profiles];
    
    // Filter by profile type if not "all"
    if (profileType !== 'all') {
      result = result.filter(profile => profile.profileType === profileType);
    }
    
    // Filter by search term if not empty
    if (searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(profile => {
        // Check user name
        const nameMatch = profile.name && profile.name.toLowerCase().includes(term);
        
        // Check company name (different for each profile type)
        let companyMatch = false;
        if (profile.profileType === 'investor' && profile.profileData?.company_or_fund) {
          companyMatch = profile.profileData.company_or_fund.toLowerCase().includes(term);
        } else if (profile.profileType === 'service_provider' && profile.profileData?.company) {
          companyMatch = profile.profileData.company.toLowerCase().includes(term);
        } else if (profile.profileType === 'pharmaceutical' && profile.profileData?.company) {
          companyMatch = profile.profileData.company.toLowerCase().includes(term);
        } else if (profile.profileType === 'startup' && profile.profileData?.mainProduct?.name) {
          companyMatch = profile.profileData.mainProduct.name.toLowerCase().includes(term);
        }
        
        return nameMatch || companyMatch;
      });
    }
    
    setFilteredProfiles(result);
    setCurrentPage(1); // Reset to first page when filters change
  }, [profiles, profileType, searchTerm]);
  
  // Calculate pagination data when filtered profiles or pagination settings change
  useEffect(() => {
    // Calculate total pages
    const calculatedTotalPages = Math.max(1, Math.ceil(filteredProfiles.length / itemsPerPage));
    setTotalPages(calculatedTotalPages);
    
    // Ensure current page is within bounds
    if (currentPage > calculatedTotalPages) {
      setCurrentPage(calculatedTotalPages);
    }
    
    // Get current page profiles
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    setCurrentProfiles(filteredProfiles.slice(indexOfFirstItem, indexOfLastItem));
  }, [filteredProfiles, currentPage, itemsPerPage]);
  
  const handleFilterChange = (e) => {
    setProfileType(e.target.value);
  };
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    // The search already happens automatically via the useEffect
  };
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of the list when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to first page when changing items per page
  };
  
  // Pagination controls component
  const PaginationControls = () => {
    // Don't show pagination if there's only one page
    if (totalPages <= 1) return null;
    
    // Calculate page numbers to show (show 5 page numbers at most)
    let pageNumbers = [];
    if (totalPages <= 5) {
      // If 5 or fewer pages, show all
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Show current page and 2 before/after when possible
      if (currentPage <= 3) {
        // Near the start
        pageNumbers = [1, 2, 3, 4, 5];
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pageNumbers = [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        // In the middle
        pageNumbers = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
      }
    }
    
    return (
      <div className="flex items-center justify-between mt-6 px-4 py-3 bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="form-select text-sm rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 mr-4"
          >
            <option value={9}>9 per page</option>
            <option value={18}>18 per page</option>
            <option value={27}>27 per page</option>
          </select>
          <span className="text-sm text-gray-700">
            Showing <span className="font-medium">{Math.min(filteredProfiles.length, (currentPage - 1) * itemsPerPage + 1)}-{Math.min(filteredProfiles.length, currentPage * itemsPerPage)}</span> of <span className="font-medium">{filteredProfiles.length}</span> profiles
          </span>
        </div>
        
        <div className="flex justify-center">
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            {/* Previous page button */}
            <button
              onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Page numbers */}
            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => handlePageChange(number)}
                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                  currentPage === number
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {number}
              </button>
            ))}
            
            {/* Next page button */}
            <button
              onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return <LoadingScreen message="Loading profiles..." />;
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Participant Profiles</h1>
          <p className="text-gray-600 mt-2">
            Browse profiles of all biotech ecosystem participants
          </p>
        </div>
      </div>
      
      {/* Search and Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
          {/* Search Form */}
          <div className="flex-grow">
            <form onSubmit={handleSearchSubmit} className="flex">
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search by name or company..."
                className="form-input flex-grow rounded-l-md border-gray-300 focus:border-primary-500 focus:ring-primary-500"
              />
              <button 
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-l-0 border-gray-300 bg-gray-50 text-gray-700 rounded-r-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </button>
            </form>
          </div>
          
          {/* Filter Dropdown */}
          <div className="w-full md:w-auto">
            <label htmlFor="profileType" className="sr-only">Filter by type</label>
            <select
              id="profileType"
              value={profileType}
              onChange={handleFilterChange}
              className="form-select w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="all">All Participants</option>
              <option value="startup">Startups</option>
              <option value="investor">Investors</option>
              <option value="service_provider">Service Providers</option>
              <option value="pharmaceutical">Pharmaceutical Companies</option>
            </select>
          </div>
        </div>
      </div>
      
      {error && (
        <div className="p-4 mb-6 bg-red-100 text-red-700 rounded-md" role="alert">
          {error}
        </div>
      )}
      
      {filteredProfiles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">No profiles found</h3>
          <p className="mt-2 text-gray-500">
            {searchTerm 
              ? `No results match your search "${searchTerm}". Try different keywords or clear the search.`
              : profileType === 'all' 
                ? 'There are no participant profiles available.' 
                : `There are no ${profileType.replace('_', ' ')} profiles available.`
            }
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentProfiles.map((profile, index) => (
              <ProfileCard key={`${profile.id}-${profile.profileType}-${index}`} profile={profile} />
            ))}
          </div>
          
          <PaginationControls />
        </>
      )}
    </div>
  );
};

export default ParticipantProfilesPage; 